<!-- PWA Update Notification -->
@if (pwaService.hasUpdate()) {
  <div
    class="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-[9999] max-w-md w-[calc(100%-2rem)] sm:w-auto animate-slide-up"
  >
    <div
      class="bg-slate-900/95 backdrop-blur-xl border border-orange-500/30 text-white p-4 sm:p-5 rounded-2xl shadow-2xl flex items-center gap-4"
    >
      <div
        class="w-10 h-10 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center flex-shrink-0"
      >
        <svg
          class="w-6 h-6 animate-spin text-orange-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-bold text-white tracking-tight">Nouvelle version disponible</p>
        <p class="text-xs text-slate-300 mt-0.5">Mettez à jour pour profiter des dernières fonctionnalités.</p>
      </div>
      <button
        (click)="onReloadUpdate()"
        class="px-3.5 py-2 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-orange-500/25 flex-shrink-0 cursor-pointer"
      >
        Actualiser
      </button>
    </div>
  </div>
}

<!-- PWA Install Prompt Banner / Toast -->
@if (pwaService.showInstallToast() && !pwaService.isInstalled() && !pwaService.isStandalone()) {
  <div
    class="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[9998] max-w-md w-auto animate-slide-up"
    role="dialog"
    aria-label="Installer l'application Gymo"
  >
    <div
      class="bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border border-slate-200/80 dark:border-slate-800 rounded-3xl p-5 shadow-[0_20px_50px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col gap-4 relative overflow-hidden"
    >
      <!-- Top decorative accent line -->
      <div
        class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500"
      ></div>

      <!-- Close cross button -->
      <button
        (click)="onDismiss()"
        class="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
        aria-label="Fermer la notification d'installation"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- App Header & Info -->
      <div class="flex items-start gap-3.5 pr-6">
        <div class="relative flex-shrink-0">
          <div
            class="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 p-0.5 shadow-md flex items-center justify-center overflow-hidden"
          >
            <img
              src="/assets/images/gymo-maskable.png"
              alt="Gymo Logo"
              class="w-full h-full object-cover rounded-[14px]"
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            />
            <div class="hidden w-full h-full items-center justify-center font-black text-white text-lg">
              G
            </div>
          </div>
        </div>

        <div class="flex flex-col">
          <div class="flex items-center gap-2">
            <h3 class="font-extrabold text-slate-900 dark:text-white text-base tracking-tight leading-tight">
              Installer Gymo
            </h3>
          </div>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
            Ajoutez l'application sur votre appareil pour un accès instantané et une expérience fluide hors-ligne.
          </p>
        </div>
      </div>

      <!-- iOS Specific Guidance -->
      @if (pwaService.isIOS()) {
        <div
          class="bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-900/50 rounded-2xl p-3.5 text-xs text-slate-700 dark:text-slate-300 flex flex-col gap-2"
        >
          <div class="font-semibold text-orange-800 dark:text-orange-300 flex items-center gap-1.5">
            <svg class="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Installation sur iPhone / iPad :
          </div>
          <ol class="space-y-1.5 pl-5 list-decimal text-[11px] text-slate-600 dark:text-slate-300">
            <li>
              Appuyez sur l'icône <strong class="text-slate-900 dark:text-white">Partager</strong> 
              <svg class="w-3.5 h-3.5 inline mx-0.5 align-text-bottom text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              en bas de Safari.
            </li>
            <li>
              Faites défiler vers le bas et sélectionnez <strong class="text-slate-900 dark:text-white">« Sur l'écran d'accueil »</strong>.
            </li>
          </ol>
        </div>
      }

      <!-- Action Buttons -->
      <div class="flex items-center gap-2.5 pt-1">
        <button
          type="button"
          (click)="onDismiss()"
          class="flex-1 py-2.5 px-4 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-center cursor-pointer"
        >
          Plus tard
        </button>

        @if (!pwaService.isIOS()) {
          <button
            type="button"
            (click)="onInstall()"
            [disabled]="isInstalling"
            class="flex-1 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 active:scale-[0.98] transition-all shadow-md shadow-orange-500/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            @if (isInstalling) {
              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Installation...</span>
            } @else {
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Installer</span>
            }
          </button>
        } @else {
          <button
            type="button"
            (click)="onDismiss()"
            class="flex-1 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 active:scale-[0.98] transition-all text-center cursor-pointer"
          >
            J'ai compris
          </button>
        }
      </div>
    </div>
  </div>
}
